import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

const AdmissionEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', emailStatus: '' });
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', notes: '', followUpDate: '' });

  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'in_process', label: 'In Process', color: 'bg-purple-100 text-purple-800' },
    { value: 'admitted', label: 'Admitted', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'withdrawn', label: 'Withdrawn', color: 'bg-gray-100 text-gray-800' }
  ];

  const emailStatusOptions = [
    { value: 'sent', label: 'Sent', color: 'bg-green-100 text-green-800' },
    { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' }
  ];

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10, ...filters };
      const response = await apiService.getAdmissionEnquiries(params);

      if (response.success) {
        setEnquiries(response.data.enquiries);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError('Failed to fetch admission enquiries');
      }
    } catch (err) {
      console.error('Error fetching admission enquiries:', err);
      setError('Failed to fetch admission enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, [currentPage, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const openModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setUpdateData({
      status: enquiry.status || '',
      notes: enquiry.notes || '',
      followUpDate: enquiry.followUpDate ? enquiry.followUpDate.split('T')[0] : ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEnquiry(null);
    setUpdateData({ status: '', notes: '', followUpDate: '' });
  };

  const handleStatusUpdate = async () => {
    if (!selectedEnquiry) return;
    try {
      const response = await apiService.updateAdmissionEnquiryStatus(selectedEnquiry.id, updateData);
      if (response.success) {
        await fetchEnquiries();
        closeModal();
      } else {
        setError('Failed to update enquiry status');
      }
    } catch (err) {
      console.error('Error updating enquiry status:', err);
      setError('Failed to update enquiry status');
    }
  };

  const getStatusBadge = (status, options) => {
    const opt = options.find(o => o.value === status);
    return opt || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && enquiries.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <i className="fas fa-spinner fa-spin text-2xl text-crimson"></i>
        <span className="ml-2 text-gray-500">Loading admission enquiries...</span>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate">Admission Enquiries</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Filter by Status</label>
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
              <option value="">All Statuses</option>
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Filter by Email Status</label>
            <select value={filters.emailStatus} onChange={(e) => handleFilterChange('emailStatus', e.target.value)}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
              <option value="">All Email Statuses</option>
              {emailStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={() => { setFilters({ status: '', emailStatus: '' }); setCurrentPage(1); }}
              className="px-4 py-2 bg-gray-100 text-slate rounded-lg hover:bg-gray-200">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Enquiries Table */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm overflow-hidden">
        {enquiries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No admission enquiries found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Info</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {enquiries.map(enquiry => {
                    const statusBadge = getStatusBadge(enquiry.status, statusOptions);
                    const emailBadge = getStatusBadge(enquiry.emailStatus, emailStatusOptions);
                    return (
                      <tr key={enquiry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate">{enquiry.name}</div>
                          <div className="text-xs text-gray-500">{enquiry.city}, {enquiry.state}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate">{enquiry.mobile}</div>
                          <div className="text-xs text-gray-500">{enquiry.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate">{enquiry.course}</div>
                          <div className="text-xs text-gray-500">{enquiry.branch}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge.color}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${emailBadge.color}`}>
                            {emailBadge.label}
                          </span>
                          {enquiry.emailSentAt && (
                            <div className="text-xs text-gray-500 mt-1">{formatDate(enquiry.emailSentAt)}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(enquiry.createdAt)}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => openModal(enquiry)} className="text-blue-600 hover:text-blue-800">
                            <i className="fas fa-edit"></i> Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center p-4 border-t">
                <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 text-sm">Previous</button>
                  <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 text-sm">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Update Modal */}
      {isModalOpen && selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1000 }}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate">Update Enquiry - {selectedEnquiry.name}</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate mb-2">Student Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>Name:</strong> {selectedEnquiry.name}</div>
                  <div><strong>Mobile:</strong> {selectedEnquiry.mobile}</div>
                  <div><strong>Email:</strong> {selectedEnquiry.email}</div>
                  <div><strong>City:</strong> {selectedEnquiry.city}</div>
                  <div><strong>State:</strong> {selectedEnquiry.state}</div>
                  <div><strong>Course:</strong> {selectedEnquiry.course}</div>
                  <div><strong>Branch:</strong> {selectedEnquiry.branch}</div>
                  <div><strong>Submitted:</strong> {formatDate(selectedEnquiry.createdAt)}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Status</label>
                <select value={updateData.status} onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
                  <option value="">Select status</option>
                  {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Notes</label>
                <textarea value={updateData.notes} onChange={(e) => setUpdateData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="4" placeholder="Add notes about this enquiry..."
                  className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Follow-up Date</label>
                <input type="date" value={updateData.followUpDate}
                  onChange={(e) => setUpdateData(prev => ({ ...prev, followUpDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate mb-2">Email Status</h3>
                <div className="flex items-center gap-4">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(selectedEnquiry.emailStatus, emailStatusOptions).color}`}>
                    {getStatusBadge(selectedEnquiry.emailStatus, emailStatusOptions).label}
                  </span>
                  {selectedEnquiry.emailSentAt && (
                    <span className="text-sm text-gray-500">Sent: {formatDate(selectedEnquiry.emailSentAt)}</span>
                  )}
                </div>
                {selectedEnquiry.emailError && (
                  <div className="mt-2 text-sm text-red-600"><strong>Error:</strong> {selectedEnquiry.emailError}</div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-100 text-slate rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleStatusUpdate}
                className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90">
                Update Enquiry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionEnquiries;
